import { IoCloseSharp } from "react-icons/io5";
import axios from "../../config/axios";
import live_display_socket from "../../config/live_display_socket";
import { useEffect, useState, useRef } from "react";
import { useTV } from "../../hooks/useTV";
import useGlobalStore from "../../store/global.store";

const RideChangeModal = ({ device, currentRide }) => {
  const { isTv } = useTV();
  const { setShowRideChange } = useGlobalStore();
  const [rides, setRides] = useState([]);
  const [focusedButton, setFocusedButton] = useState(null);
  const firstRideButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const exitButtonRef = useRef(null);
  const buttonRefs = useRef({});
  const [loading, setLoading] = useState(true);
  const [focusIndex, setFocusIndex] = useState(0);

  useEffect(() => {
    if (!currentRide) return;
    setLoading(true);
    axios
      .get("/rides?parkId=" + currentRide._id.split("_")[0])
      .then((response) => {
        setRides(response.data.rides);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus first ride button when rides are loaded in TV mode
  useEffect(() => {
    if (isTv && rides.length > 0 && firstRideButtonRef.current) {
      const firstRide = rides[0];
      setFocusedButton(`ride-${firstRide._id}`);
      setFocusIndex(0);
      firstRideButtonRef.current.focus();
    }
  }, [isTv, rides]);

  // Set up keyboard navigation for TV mode
  useEffect(() => {
    if (!isTv) return;

    const handleKeyDown = (e) => {
      if (loading || rides.length === 0) return;

      // Close button has index -1, ride buttons start at index 0
      if (e.key === "ArrowDown") {
        e.preventDefault();

        if (focusIndex === -1) {
          // Move from close button to first ride
          setFocusIndex(0);
          const firstRide = rides[0];
          setFocusedButton(`ride-${firstRide._id}`);
          if (buttonRefs.current[`ride-${firstRide._id}`]) {
            buttonRefs.current[`ride-${firstRide._id}`].focus();
            buttonRefs.current[`ride-${firstRide._id}`].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        } else if (focusIndex === rides.length - 1) {
          // Move from last ride to exit button
          setFocusIndex(rides.length);
          setFocusedButton("exit");
          if (exitButtonRef.current) {
            exitButtonRef.current.focus();
            exitButtonRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        } else if (focusIndex < rides.length - 1) {
          // Move to next ride button
          const nextIndex = focusIndex + 1;
          const nextRide = rides[nextIndex];
          setFocusIndex(nextIndex);
          setFocusedButton(`ride-${nextRide._id}`);
          if (buttonRefs.current[`ride-${nextRide._id}`]) {
            buttonRefs.current[`ride-${nextRide._id}`].focus();
            buttonRefs.current[`ride-${nextRide._id}`].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();

        if (focusIndex === rides.length) {
          // Move from exit button to last ride
          const lastIndex = rides.length - 1;
          const lastRide = rides[lastIndex];
          setFocusIndex(lastIndex);
          setFocusedButton(`ride-${lastRide._id}`);
          if (buttonRefs.current[`ride-${lastRide._id}`]) {
            buttonRefs.current[`ride-${lastRide._id}`].focus();
            buttonRefs.current[`ride-${lastRide._id}`].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        } else if (focusIndex === 0) {
          // Move from first ride to close button
          setFocusIndex(-1);
          setFocusedButton("close");
          if (closeButtonRef.current) {
            closeButtonRef.current.focus();
            closeButtonRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        } else if (focusIndex > 0) {
          // Move to previous ride button
          const prevIndex = focusIndex - 1;
          const prevRide = rides[prevIndex];
          setFocusIndex(prevIndex);
          setFocusedButton(`ride-${prevRide._id}`);
          if (buttonRefs.current[`ride-${prevRide._id}`]) {
            buttonRefs.current[`ride-${prevRide._id}`].focus();
            buttonRefs.current[`ride-${prevRide._id}`].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isTv, rides, loading, focusedButton, focusIndex]);

  const updateDeviceView = (ride_id) => {
    if (isTv) document.getElementById("changeRideButton").focus();
    setShowRideChange(false);
    axios
      .patch(
        `/users/device/${device._id}`,
        { screen_view: `Ride__${ride_id}` },
        { withCredentials: true }
      )
      .then(() => {
        live_display_socket.emit("remote_refresh", {
          device_id: device?._id,
        });
      })
      .catch((error) => {});
  };

  // Button style with focus handling
  const getButtonStyle = (buttonId, baseStyle) => {
    const focusedStyle =
      isTv && focusedButton === buttonId
        ? {
            border: "6px solid #3ADF3C",
            outline: "none",
          }
        : {};

    return {
      ...baseStyle,
      ...focusedStyle,
    };
  };

  // Base ride button style
  const baseRideButtonStyle = {
    marginLeft: "10px",
    border: isTv ? "6px solid blue" : "2px solid blue",
    background: "transparent",
    color: "white",
    fontFamily: "Lato",
    fontSize: isTv ? "50px" : "40px",
    padding: isTv ? "10px 30px" : "0px 20px",
    borderRadius: "20px",
    fontWeight: "700",
    margin: "10px 0px",
    cursor: "pointer",
  };

  // Exit button style
  const exitButtonStyle = {
    marginLeft: "10px",
    border: isTv ? "6px solid red" : "2px solid red",
    background: "transparent",
    color: "red",
    fontFamily: "Lato",
    fontSize: isTv ? "50px" : "40px",
    padding: isTv ? "10px 30px" : "0px 20px",
    borderRadius: "20px",
    fontWeight: "700",
    margin: "20px 0px",
    cursor: "pointer",
    marginBottom: window.innerHeight,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "absolute",
        top: "0%",
        backgroundColor: "white",
        width: "100vw",
        height: "100vh",
        zIndex: 50,
        opacity: "80%",
      }}
    >
      {!isTv && (
        <button
          tabIndex={0}
          ref={closeButtonRef}
          onFocus={() => {
            setFocusedButton("close");
            setFocusIndex(-1);
          }}
          onBlur={() => setFocusedButton(null)}
          onClick={() => {
            setShowRideChange(false);
            if (isTv) document.getElementById("changeRideButton").focus();
          }}
          style={{
            position: "absolute",
            top: "5%",
            right: "3%",
            background: "transparent",
            border:
              isTv && focusedButton === "close" ? "6px solid #3ADF3C" : "none",
            borderRadius: "50%",
            padding: "5px",
            cursor: "pointer",
            outline: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IoCloseSharp size={isTv ? 100 : 60} />
        </button>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "start",
          height: "80vh",
          width: "80vw",
          overflowY: "scroll",
          marginBottom: "50px",
        }}
      >
        {loading ? (
          <p style={{ fontSize: isTv ? "40px" : "24px", color: "blue" }}>
            Loading rides...
          </p>
        ) : (
          rides.map((ride, index) => {
            const buttonId = `ride-${ride._id}`;
            return (
              <button
                key={index}
                ref={(el) => {
                  if (index === 0) firstRideButtonRef.current = el;
                  buttonRefs.current[buttonId] = el;
                }}
                tabIndex={0}
                onFocus={() => {
                  setFocusedButton(buttonId);
                  setFocusIndex(index);
                }}
                onBlur={() => setFocusedButton(null)}
                style={getButtonStyle(buttonId, baseRideButtonStyle)}
                onClick={() => updateDeviceView(ride._id)}
              >
                <span
                  style={{
                    fontFamily: "Lato",
                    fontWeight: "700",
                    color: "blue",
                  }}
                >
                  {ride.name}
                </span>
              </button>
            );
          })
        )}

        {isTv && !loading && (
          <button
            tabIndex={0}
            ref={exitButtonRef}
            onFocus={() => {
              setFocusedButton("exit");
              setFocusIndex(rides.length);
            }}
            onBlur={() => setFocusedButton(null)}
            onClick={() => {
              setShowRideChange(false);
              document.getElementById("changeRideButton").focus();
            }}
            style={getButtonStyle("exit", exitButtonStyle)}
          >
            Exit
          </button>
        )}
      </div>
    </div>
  );
};

export default RideChangeModal;
